import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Fab,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SearchIcon from "@mui/icons-material/Search";
import ImgPath from "../../Constants/Imgs";
import { useNavigate } from "react-router-dom";
import ColorData from "Components/CstmTheme/ColorData";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
type MobileDrawerProps = {
  open: boolean;
  onClose: () => void;
  navItems: {
    label: string;
    submenu: string[];
  }[];
  expandedItems: { [key: string]: boolean };
  onExpandClick: (itemLabel: string) => void;
  onNavigate: (item: string) => void;
};

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  open,
  onClose,
  navItems,
  expandedItems,
  onExpandClick,
  onNavigate,
}) => {
  const navigate = useNavigate();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          maxWidth: 400,
          backgroundColor: ColorData.primary.main,
          color: ColorData.text.secondary,
        },
      }}
    >
      <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            px: 6,
            paddingTop: "20px",
          }}
        >
          <Box
            component="img"
            src={"https://nolegalpaisa.com/assets/images/nlp_logo.png"}
            alt="Logo"
            sx={{
              width: { xs: "150px", sm: "200px", md: "100px" },
              height: { xs: "60px", sm: "80px", md: "83px" },
              objectFit: "contain",
              cursor: "pointer",
              filter: "drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.3))",
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
            onClick={() => {
              navigate("/");
              onClose();
            }}
          />

          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Navigation Items */}
        <List sx={{ flex: 1, p: 3 }}>
          {navItems.map((item) => (
            <Box key={item.label}>
              <ListItemButton
                onClick={() => onExpandClick(item.label)}
                sx={{
                  paddingY: 0.5,
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  justifyContent: "flex-start",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: ColorData.text.secondary,
                    }}
                    // sx={{ m: 0 }}
                  />
                  {expandedItems[item.label] ? (
                    <ArrowDropDownIcon
                      sx={{ color: ColorData.text.secondary, fontSize: 20 }}
                    />
                  ) : (
                    <ArrowDropDownIcon
                      sx={{ color: ColorData.text.secondary, fontSize: 20 }}
                    />
                  )}
                </Box>
              </ListItemButton>

              <Collapse
                in={expandedItems[item.label]}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {item.submenu.map((subItem) => (
                    <ListItem
                      key={subItem}
                      disableGutters
                      sx={{
                        py: 0,
                        px: 2,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        borderRadius: 1,
                        "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                      }}
                    >
                      <ListItemButton
                        sx={{
                          paddingY: "0.25rem",
                          paddingX: "1rem",
                          borderBottom: "1px solid rgba(255,255,255,0.05)",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                          },
                        }}
                        onClick={() => {
                          onClose();
                          onNavigate(subItem);
                        }}
                      >
                        <ListItemText
                          primary={subItem}
                          primaryTypographyProps={{
                            fontSize: "14px",
                            color: ColorData.text.secondary,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          ))}
        </List>

        {/* Search Box */}
        <Box sx={{ p: 3, px: 6, mt: "auto" }}>
          <TextField
            fullWidth
            placeholder="Search..."
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "#9f4c52", mr: 1 }} />,
              sx: {
                backgroundColor: "#fff",
                borderRadius: 1,
                height: 36, // 👈 reduce height to match Figma
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "2px solid #fff",
                },
                px: 1, // 👈 reduce horizontal padding inside
              },
            }}
            sx={{
              "& .MuiInputBase-input": {
                color: "#333",
                fontSize: "14px",
                py: 0.5, // 👈 reduce top/bottom padding
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#666",
                opacity: 1,
              },
            }}
          />
        </Box>

        {/* WhatsApp Button */}
        <Fab
          sx={{
            position: "absolute",
            bottom: 80,
            right: 20,
            backgroundColor: "#25D366",
            color: "#fff",
            "&:hover": { backgroundColor: "#128C7E" },
          }}
          onClick={() => {
            window.open("https://wa.me/919326024128", "_blank");
          }}
        >
          <WhatsAppIcon />
        </Fab>
      </Box>
    </Drawer>
  );
};

export default MobileDrawer;
